

import { Link } from 'react-router-dom';
import { Users, MapPin, Crown, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { REGIONS, THEMES } from '@/lib/game-logic/constants';
import type { Game } from '@/lib/db/schema';

interface GameCardProps {
  game: Game;
  isOwner: boolean;
}

const STATUS_LABELS: Record<Game['status'], { label: string; color: string }> = {
  setup: { label: 'Setting up', color: 'text-amber-400 bg-amber-400/10' },
  character_creation: { label: 'Creating characters', color: 'text-blue-400 bg-blue-400/10' },
  active: { label: 'In progress', color: 'text-green-400 bg-green-400/10' },
  paused: { label: 'Paused', color: 'text-neutral-400 bg-neutral-400/10' },
};

export function GameCard({ game, isOwner }: GameCardProps) {
  const region = REGIONS[game.region] ?? { name: game.region, faction: 'Unknown', description: '', vibe: '' };
  const theme = THEMES[game.theme] ?? { name: game.theme, vitals: 0, resolve: 0, description: '' };
  const status = STATUS_LABELS[game.status] ?? { label: game.status, color: 'text-neutral-500 bg-neutral-500/10' };

  const href = game.status === 'setup'
    ? `/game/${game.id}/setup`
    : game.status === 'character_creation'
    ? `/game/${game.id}/character`
    : `/game/${game.id}/play`;

  return (
    <Link
      to={href}
      className="block bg-neutral-950 border border-neutral-800 rounded-lg p-4 hover:border-neutral-700 transition-colors group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white truncate group-hover:text-neutral-200">
            {game.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1">
            <MapPin className="w-3 h-3 text-neutral-500" />
            <span className="text-xs text-neutral-500">{region.name}</span>
          </div>
        </div>
        {isOwner && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/10 rounded text-amber-400">
            <Crown className="w-3 h-3" />
            <span className="text-xs">Owner</span>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-4 text-xs text-neutral-500">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {game.maxPlayers} players max
          </span>
          <span>{theme.name}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
        <span className={cn('text-xs px-2 py-0.5 rounded', status.color)}>
          {status.label}
        </span>
        <span className="flex items-center gap-1 text-xs text-neutral-600">
          <Clock className="w-3 h-3" />
          {formatRelativeTime(game.updatedAt)}
        </span>
      </div>
    </Link>
  );
}

function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - dateObj.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return dateObj.toLocaleDateString();
}
